import React, { useCallback, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import './BoostsPanel.css';
import * as st from '../../core/st';
import {
    boostByKey,
    boostRowsForLevel,
    isBoostSelected,
    selectedCastingIsCritical,
    selectedGroupKeys,
    valueAtCount,
} from '../../core/boosts';
import { fmtCondLabel, getTypeId } from '../../core/spellUtils';
import BoostDetails, { buffConditionText } from './BoostDetails';
import ChargesPicker from '../utils/ChargesPicker';
import MediaImg from '../utils/MediaImg';
import Tooltip from '../utils/Tooltip';

const TXT_CRIT_TOGGLE = {
    fr: 'Coup critique',
    en: 'Critical hit',
    de: 'Kritischer Treffer',
    es: 'Golpe crítico',
    pt: 'Golpe crítico',
};
const TXT_LEVEL_REQUIRED = {
    fr: 'Niveau {n} requis',
    en: 'Level {n} required',
    de: 'Stufe {n} erforderlich',
    es: 'Nivel {n} requerido',
    pt: 'Nível {n} necessário',
};
// The Spells tab's disclaimer, word for word: both tabs are read off the same
// spell data and have the same way to be wrong.
const TXT_WIP_NOTE = {
    fr: "Cette section est en cours d'amélioration : signalez les sorts problématiques sur le serveur Discord de la communauté.",
    en: 'This section is being improved: report problematic spells on the community Discord server.',
    de: 'Dieser Bereich wird gerade verbessert: Melde problematische Zauber auf dem Discord-Server der Community.',
    es: 'Esta sección está en proceso de mejora: informa de los hechizos problemáticos en el servidor de Discord de la comunidad.',
    pt: 'Esta seção está em processo de melhoria: relate os feitiços problemáticos no servidor Discord da comunidade.',
};
const TXT_EMPTY = {
    fr: 'Aucun boost disponible.',
    en: 'No boost available.',
    de: 'Kein Boost verfügbar.',
    es: 'Ningún boost disponible.',
    pt: 'Nenhum boost disponível.',
};

const pick = (dict, lang) => dict?.[lang] || dict?.en || dict?.fr || '';
const fill = (dict, lang, n) => pick(dict, lang).replace('{n}', String(n));

const statDefFor = (id) => st.STATS.find((s) => s.id === id) || null;
/** The game's own critical icon — the same art the % Critique stat wears. */
const CRIT_ICON = statDefFor(st.CRIT_ID)?.icon;

/**
 * One buff of a boost as a row the player picks: its values, and its charges
 * picker when it stacks. A single-buff boost's row also carries the boost's
 * name and crit toggle (`head`); a boost holding several buffs shows those
 * once above its rows, and labels each row with what fires it instead.
 */
const BuffRow = ({
    row, group, head = null, label = null, shown, count, selected, canPress, selectable, lang, onToggle, setCharges,
}) => (
    <div
        className={[
            'boost-card-row',
            canPress ? 'boost-card-row--selectable' : '',
            selected ? 'boost-card-row--selected' : '',
        ].filter(Boolean).join(' ')}
        role={canPress ? 'button' : undefined}
        tabIndex={canPress ? 0 : undefined}
        aria-pressed={canPress ? selected : undefined}
        onClick={canPress ? onToggle : undefined}
        onKeyDown={canPress ? (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            onToggle();
        } : undefined}
    >
        {/* Name (or condition), crit toggle and charge picker wrap inside this
            half, so the value keeps its place on the first line instead of
            dropping under them. */}
        <span className="boost-card-label">
            {head}
            {label ? <span className="boost-card-group-label">{label}</span> : null}
            {group && group.maxCharges > 1 && shown.length ? (
                <ChargesPicker
                    value={count}
                    max={group.maxCharges}
                    name={group.stackLabel ? pick(group.stackLabel, lang) : null}
                    labels={group.stackConditions?.map((conditions) => fmtCondLabel(conditions[0]?.id, null, lang))}
                    lang={lang}
                    disabled={!selectable}
                    onChange={(n) => setCharges(row.key, n, group.maxCharges, group.index)}
                />
            ) : null}
        </span>
        <span className="boost-card-row-value">
            {!shown.length ? (
                <span className="boost-card-row-locked">
                    {fill(TXT_LEVEL_REQUIRED, lang, row.requiredLevel)}
                </span>
            ) : shown.map((option) => (
                <span key={option.key} className="boost-card-row-stat">
                    <span className="boost-card-row-amount">
                        {`+${valueAtCount(option.value, option.stackValues, count)}${statDefFor(option.characteristic)?.unit || ''}`}
                    </span>
                    {statDefFor(option.characteristic)?.icon ? (
                        <img
                            className="boost-card-row-icon"
                            src={statDefFor(option.characteristic).icon}
                            alt={pick(statDefFor(option.characteristic).label, lang)}
                        />
                    ) : null}
                </span>
            ))}
        </span>
    </div>
);

/**
 * One boost: each of its buffs picked or not, with the critical toggle beside
 * the name saying which casting the picks mean. Two rows per buff (one per
 * casting) would read as two things to pick when they are one thing with a
 * setting — and made the arithmetic look additive, which it is not.
 */
const BoostCard = ({
    row, selection, selectable, lang, critPreference, setCritPreference, apply, charges, setCharges,
}) => {
    const locked = row.options.length === 0;
    const selected = isBoostSelected(selection, row.key);
    const critOn = selected
        ? selectedCastingIsCritical(selection, row.key)
        : critPreference[row.key] === true;
    const groups = row.groups.length ? row.groups : [{ index: 0, maxCharges: 1 }];
    // Falls back to the ordinary casting for a buff that has no critical one,
    // so such a card simply shows no toggle.
    const castingOptions = (crit, group) => {
        const own = row.options.filter((option) => option.group === group);
        const matching = own.filter((option) => option.isCritical === crit);
        return matching.length ? matching : own.filter((o) => !o.isCritical);
    };
    // A critical casting that grants exactly what the ordinary one does is not
    // a choice, so it gets no toggle either.
    const castingSignature = (crit) => groups
        .flatMap((group) => castingOptions(crit, group.index).map((o) => `${group.index}:${o.characteristic}:${o.value}`))
        .sort().join('|');
    const hasCrit = row.options.some((option) => option.isCritical)
        && castingSignature(true) !== castingSignature(false);

    const canPress = selectable && !locked;
    const pickedGroups = new Set(groups
        .filter((group) => selectedGroupKeys(selection, row.key, group.index).length)
        .map((group) => group.index));
    const keysFor = (indices, crit) => [...indices].flatMap((index) => castingOptions(crit, index).map((o) => o.key));
    const toggleGroup = (index) => {
        if (!canPress) return;
        const next = new Set(pickedGroups);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        apply(row.key, keysFor(next, critOn));
    };
    const toggleCrit = () => {
        if (!canPress) return;
        setCritPreference((prev) => ({ ...prev, [row.key]: !critOn }));
        if (pickedGroups.size) apply(row.key, keysFor(pickedGroups, !critOn));
    };
    // No stored count means the most the buff can hold.
    const countFor = (group) => Math.min(group.maxCharges, Math.max(1, charges?.[String(group.index)] || group.maxCharges));

    const critButton = hasCrit && CRIT_ICON ? (
        // Its own control inside a row that is itself a button: pressing it
        // must change the casting, not the pick.
        <button
            type="button"
            className={`boost-card-crit${critOn ? ' boost-card-crit--on' : ''}`}
            aria-label={pick(TXT_CRIT_TOGGLE, lang)}
            aria-pressed={critOn}
            title={pick(TXT_CRIT_TOGGLE, lang)}
            disabled={!canPress}
            onClick={(e) => { e.stopPropagation(); toggleCrit(); }}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <img src={CRIT_ICON} alt="" />
        </button>
    ) : null;
    const nameAndCrit = (
        <>
            <span className="boost-card-name">{pick(row.name, lang)}</span>
            {critButton}
        </>
    );
    const buffRow = (group, props) => (
        <BuffRow
            key={group.index}
            row={row}
            group={group}
            shown={locked ? [] : castingOptions(critOn, group.index)}
            count={countFor(group)}
            selected={pickedGroups.has(group.index)}
            canPress={canPress}
            selectable={selectable}
            lang={lang}
            onToggle={() => toggleGroup(group.index)}
            setCharges={setCharges}
            {...props}
        />
    );

    return (
        <div
            className={`boost-card${locked ? ' boost-card--locked' : ''}`}
            role="group"
            aria-label={pick(row.name, lang)}
        >
            <Tooltip
                content={<BoostDetails row={row} lang={lang} />}
                className="boost-card-detail-tooltip"
                positionMode="side"
                positionOptions={{ estimatedWidth: 260, useRightEdge: true }}
            >
                <div className="boost-card-body">
                    <MediaImg
                        className="boost-card-icon"
                        src={row.icon}
                        alt={pick(row.name, lang)}
                        loading="lazy"
                    />
                    {groups.length === 1 ? buffRow(groups[0], { head: nameAndCrit }) : (
                        <div className="boost-card-buffs">
                            <span className="boost-card-head">{nameAndCrit}</span>
                            {groups.map((group) => buffRow(group, { label: buffConditionText(group, lang) }))}
                        </div>
                    )}
                </div>
            </Tooltip>
        </div>
    );
};

/**
 * The generator's Boosts tab: self-buffs the character is assumed to have cast.
 *
 * A boost is not a generation criterion but a natural stat of the character:
 * a selection changes the build's own sheet and what the solver starts from
 * (Build.getSolverBoostEffects), so this panel edits the Build whenever the
 * build is editable, generator on or off. Offered: the class's boosts, the
 * passives of the items worn, and the common ones.
 */
const BoostsPanel = ({
    build,
    setBuild,
    lvl,
    lang = 'fr',
    isGenerating = false,
    canEditBuild = true,
    genConfig = null,
    setGenConfig = null,
    // The "section being improved" note; hidden with the generator toggled off.
    showNote = true,
}) => {
    const cls = build?.getCharacterClass?.() || build?.characterClass || null;
    const breedId = getTypeId(cls?.name);
    // Worn items, by id: their passives are boosts while they are worn.
    const items = build?.items;
    const equippedById = useMemo(
        () => new Map((items || []).filter(Boolean).map((item) => [Number(item.id), item])),
        [items],
    );
    const rows = useMemo(
        () => boostRowsForLevel(lvl, breedId, new Set(equippedById.keys())).map((row) => {
            if (row.itemId == null) return row;
            // An item passive is shown as the item it comes from: its name and
            // icon, with the passive's own name left to the description.
            const item = equippedById.get(row.itemId);
            return { ...row, name: row.itemName || row.name, icon: item?.icon || row.icon };
        }),
        [lvl, breedId, equippedById],
    );
    // The class's own boosts, then the passives of what the build wears, then
    // the common ones, split by dividers. Empty groups are dropped.
    const groups = useMemo(() => [
        { key: 'class', rows: rows.filter((row) => !row.isCommon && row.itemId == null) },
        { key: 'items', rows: rows.filter((row) => row.itemId != null) },
        { key: 'common', rows: rows.filter((row) => row.isCommon) },
    ].filter((group) => group.rows.length), [rows]);
    const selection = build?.boosts || {};
    const selectable = canEditBuild && !isGenerating;

    // Which casting each card shows while its boost is *off*. Once it is on the
    // build itself is the answer, so this is only ever a starting point.
    const [critPreference, setCritPreference] = useState({});

    const apply = useCallback((boostKey, optionKeys) => {
        if (!build || !setBuild || !selectable) return;
        setBuild(build.setBoostOptions(boostKey, optionKeys));
        // When boosts count for any criteria, an item's passive counts as a
        // natural stat of the character (Build.getSolverBoostEffects), so the
        // generator must keep the item that gives it: picking the passive locks
        // the item's slot, unpicking it unlocks the slot again.
        const itemId = boostByKey(boostKey)?.item_id;
        const boostsCount = genConfig?.useBoostsForStats === true || genConfig?.useBoostsForSpells !== false;
        if (itemId == null || !genConfig || !setGenConfig || !boostsCount) return;
        const slot = (build.items || []).findIndex((item) => item && Number(item.id) === Number(itemId));
        if (slot < 0) return;
        const locked = { ...(genConfig.lockedSlots || {}) };
        if (optionKeys?.length) locked[slot] = build.items[slot];
        else delete locked[slot];
        setGenConfig(genConfig.update('lockedSlots', locked));
    }, [build, setBuild, selectable, genConfig, setGenConfig]);

    const setCharges = useCallback((boostKey, n, max, group) => {
        if (!build || !setBuild || !selectable) return;
        setBuild(build.setBoostCharges(boostKey, n, max, group));
    }, [build, setBuild, selectable]);

    if (!rows.length) {
        return (
            <div className="boosts-panel-wrap">
                <div className="boosts-panel-msg">{pick(TXT_EMPTY, lang)}</div>
            </div>
        );
    }

    return (
        <div className="boosts-panel-wrap">
            <div className="boosts-panel-content">
                {groups.map((group, index) => (
                    <React.Fragment key={group.key}>
                        {index > 0 ? <hr className="boosts-panel-divider" /> : null}
                        <div className="boosts-panel-grid">
                            {group.rows.map((row) => (
                                <BoostCard
                                    key={row.key}
                                    row={row}
                                    selection={selection}
                                    selectable={selectable}
                                    lang={lang}
                                    critPreference={critPreference}
                                    setCritPreference={setCritPreference}
                                    apply={apply}
                                    charges={build?.boostCharges?.[row.key]}
                                    setCharges={setCharges}
                                />
                            ))}
                        </div>
                    </React.Fragment>
                ))}
            </div>
            {showNote ? (
                <p className="boosts-panel-note">
                    <Info className="boosts-panel-note-icon" aria-hidden="true" />
                    {pick(TXT_WIP_NOTE, lang)}
                </p>
            ) : null}
        </div>
    );
};

export default BoostsPanel;
